import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useSubscription } from "@apollo/client";
import { useGraphMutation, useToast } from "@dashboard/library";
import { useTranslation } from "react-i18next";

import {
  EvaluationCreateFormProps,
  GeneratedAIAnswersMap,
} from "@/components/EvaluationCreateForm/EvaluationCreateForm.decl";
import {
  GenerateAiAnswersMutationVariables,
  QuestionType,
} from "@/graphql/generated/graphql";
import { GenerateAIAnswers } from "@/graphql/mutations/GenerateAIAnswers";
import { OnAIAnswersGenerated } from "@/graphql/subscriptions/OnAIAnswersGenerated";

const TWO_MINUTES_IN_MILLISECONDS = 2 * 60 * 1000;

// return the categories that include AI questions
function getCategoriesInput(
  categories: EvaluationCreateFormProps["scoreCard"]["categories"]
): GenerateAiAnswersMutationVariables["input"]["categories"] {
  return categories.reduce<
    GenerateAiAnswersMutationVariables["input"]["categories"]
  >((acc, category) => {
    const aiQuestions = category.questions
      .filter((question) => question.isAIEnabled) // Filter AI-enabled questions
      .map((question) => {
        if (question.__typename === "ScoreCardBinaryQuestion") {
          return {
            questionId: question.ID,
            questionText: question.text,
            questionType: QuestionType.BINARY,
            predefinedAIQuestionId: question.predefinedAIQuestionId,
            questionPossibleBinaryAnswers: question.possibleBinaryAnswers.map(
              (question) => ({
                value: question.binaryValue,
                points: question.points,
                answerGuide: question.answerGuide,
              })
            ),
            questionStrictness: question.strictness,
          };
        }

        if (question.__typename === "ScoreCardRatingQuestion") {
          return {
            questionId: question.ID,
            questionText: question.text,
            questionType: QuestionType.RATING,
            predefinedAIQuestionId: question.predefinedAIQuestionId,
            questionPossibleRatingAnswers: question.possibleRatingAnswers.map(
              (question) => ({
                value: question.ratingValue,
                points: question.points,
                answerGuide: question.answerGuide,
              })
            ),
            questionStrictness: question.strictness,
          };
        }

        throw new Error("Invalid question type");
      });

    if (aiQuestions.length > 0) {
      return [
        ...acc,
        {
          categoryId: category.ID,
          questions: aiQuestions,
        },
      ];
    }

    return acc;
  }, []);
}

export function useGenerateAIAnswers(
  callId: EvaluationCreateFormProps["callId"],
  scorecard: EvaluationCreateFormProps["scoreCard"],
  transcriptionId: EvaluationCreateFormProps["transcriptionId"]
) {
  const [couldNotGenerateAnswers, setCouldNotGenerateAnswers] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const calledRef = useRef(false);

  const { t } = useTranslation();

  const { show } = useToast();

  const categoriesInput = useMemo(
    () => getCategoriesInput(scorecard.categories),
    [scorecard.categories]
  );

  const [mutation] = useGraphMutation(GenerateAIAnswers);

  const { data, loading: loadingGeneratedAnswers } = useSubscription(
    OnAIAnswersGenerated,
    {
      variables: {
        transcriptionId: transcriptionId as string,
      },
      skip: !transcriptionId || !categoriesInput.length,
    }
  );

  const generateAIAnswers = useCallback(() => {
    if (!transcriptionId || !categoriesInput.length) {
      return;
    }
    mutation({
      variables: {
        input: {
          callId,
          categories: categoriesInput,
          transcriptionId,
          language: scorecard.language,
        },
      },
    });
  }, [callId, mutation, scorecard.language, transcriptionId, categoriesInput]);

  useEffect(() => {
    if (!calledRef.current) {
      generateAIAnswers();
      calledRef.current = true;
    }
  }, [generateAIAnswers]);

  useEffect(() => {
    if (!data && !timeoutRef.current) {
      timeoutRef.current = setTimeout(() => {
        setCouldNotGenerateAnswers(true);

        show({
          variant: "critical",
          message: t("evaluation.could_not_generate_ai_answers"),
        });
      }, TWO_MINUTES_IN_MILLISECONDS);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [show, t, data]);

  useEffect(() => {
    if (data?.onAIAnswersGenerated?.error?.name) {
      setCouldNotGenerateAnswers(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      show({
        variant: "critical",
        message: t("evaluation.could_not_generate_ai_answers"),
      });
    }
  }, [data?.onAIAnswersGenerated?.error?.name, show, t]);

  const generatedAIAnswersMap = useMemo(() => {
    if (
      !data ||
      couldNotGenerateAnswers ||
      data?.onAIAnswersGenerated?.error?.name ||
      !data.onAIAnswersGenerated?.data?.categories
    ) {
      return undefined;
    }

    return data.onAIAnswersGenerated.data.categories.reduce<GeneratedAIAnswersMap>(
      (acc, category) => {
        const questions = category.questions.reduce(
          (acc, question) => ({
            ...acc,
            [question.questionId]: {
              ...(question.questionType === "RATING" && {
                ratingAnswer: question.ratingAnswer,
              }),
              ...(question.questionType === "BINARY" && {
                binaryAnswer: question.binaryAnswer,
              }),
              questionType: question.questionId,
              feedback: question.feedback,
            },
          }),
          {}
        );

        return {
          ...acc,
          ...questions,
        };
      },
      {}
    );
  }, [data, couldNotGenerateAnswers]);

  return {
    generatedAIAnswersMap,
    loadingGeneratedAnswers:
      loadingGeneratedAnswers && !couldNotGenerateAnswers,
  };
}
